import React, { useState, useCallback, useEffect } from "react";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Switch,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  DialogContentText,
  IconButton,
  Button,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import {
  NO_IMAGE,
  SOMETHING_WENT_WRONG,
  PRICE_UPDATED_SUCCESSFULLY,
  QUANTITY_UPDATED_SUCCESSFULLY,
  PRICE_CANNOT_BE_LESS_THAN_ZERO,
  PRICE_CANNOT_BE_MORE_THAN_MRP,
  QUANTITY_CANNOT_BE_LESS_THAN_ZERO,
} from "../../../constants/string";
import axios from "../../../network";
import { NumberFormatUtils } from "../../../utility/NumberFormatUtils";
import debounce from "lodash/debounce";
import { useWarehouse } from "../../../contexts/WarehouseContext";
import CloseIcon from "@mui/icons-material/Close";
import { AntSwitch } from "../../../components/AntSwitch";
import ImageSlider from "./ImageSlider";

const mapUnitToShortForm = (unit) => {
  const unitMapping = {
    kilogram: "kg",
    gram: "gm",
    millilitre: "ml",
    unit: "unit",
    dozen: "dz",
    tonne: "t",
    centimeter: "cm",
  };

  return unitMapping[unit.toLowerCase()] || unit;
};

const getNetQuantityValue = (netQty, netUnit, quantityUnit, quantityValue) => {
  netQty = parseInt(netQty, 10);
  quantityValue = parseInt(quantityValue, 10);

  if (netUnit === "unit") {
    const result = quantityValue / netQty;
    return `${netQty} x ${Math.ceil(result * 100) / 100} ${quantityUnit}`;
  } else {
    return `1 x ${netQty} ${netUnit}`;
  }
};

const ProductCard = ({ item, setInventoryItems, openSnackBar }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [tempQuantity, setTempQuantity] = useState(
    item?.Inventory?.availableinventory || item?.availableinventory || 0
  );
  const [tempPrice, setTempPrice] = useState(
    NumberFormatUtils(item?.Skunit?.price || item?.price || item?.mrp || 0)
  );
  const [showFullDescription, setShowFullDescription] = useState(false);
  const { warehouseId } = useWarehouse();
  const description = item?.Skunit?.description || item?.description || "";
  const MAX_DESCRIPTION_LENGTH = 250;
  const netQty = item?.Skunit?.netQuantityValue || item?.netQuantityValue || 1;
  const netUnit = mapUnitToShortForm(
    item?.Skunit?.netQuantityUnits || item?.netQuantityUnits || "unit"
  );
  const quantityUnit = mapUnitToShortForm(
    item?.Skunit?.quantityUnit || item?.quantityUnit || "gm"
  );
  const quantityValue = item?.Skunit?.quantityValue || item?.quantityValue || 1;
  const netQuantityValue = getNetQuantityValue(
    netQty,
    netUnit,
    quantityUnit,
    quantityValue
  );
  const [images, setImages] = useState(() => {
    try {
      if (item?.Skunit?.images) {
        return JSON.parse(item.Skunit.images);
      } else if (item?.images && item.images !== "{}") {
        return JSON.parse(item.images);
      }
    } catch (error) {
      console.error("Error parsing images:", error);
    }
    return {};
  });  
  useEffect(() => {
    if (images && Object.keys(images).length === 0) {
      if (item?.Skunit && item?.Skunit?.pictureUrl !== "") {
        setImages({ front: item?.Skunit?.pictureUrl });
      } else if (item?.pictureurl && item?.pictureurl !== "") {
        setImages({ front: item?.pictureurl });
      }
    }
  });
  
  const toggleDescription = () => {
    setShowFullDescription((prev) => !prev);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleQuantityChange = (id, newValue) => {
    if (newValue < 0) {
      setTempQuantity(
        item?.Inventory?.availableinventory || item?.availableinventory || 0
      );
      openSnackBar(QUANTITY_CANNOT_BE_LESS_THAN_ZERO, "error");
    } else {
      setTempQuantity(newValue);
      debouncedUpdateQuantity(id, newValue);
    }
  };

  const handleToggleChange = (id) => {
    setTempQuantity(tempQuantity > 0 ? 0 : 20);
    updateQuantity(id, tempQuantity > 0 ? 0 : 20);
  };

  const handleMrpChange = (id, newValue) => {
    if (newValue > (item?.Skunit?.mrp || item?.mrp)) {
      setTempPrice(item?.Skunit?.price || item?.mrp || 0);
      openSnackBar(PRICE_CANNOT_BE_MORE_THAN_MRP, "error");
    } else if (newValue < 0) {
      setTempPrice(item?.Skunit?.price || item?.mrp || 0);
      openSnackBar(PRICE_CANNOT_BE_LESS_THAN_ZERO, "error");
    } else {
      setTempPrice(newValue);
      debouncedUpdatePrice(id, newValue);
    }
  };

  const updateQuantity = (id, value) => {
    let formData = new FormData();
    formData.append(
      "data[Inventory][warehouse_id]",
      item?.Inventory?.warehouse_id || warehouseId
    );
    formData.append("data[Inventory][quantity]", value);
    formData.append(
      "data[Inventory][availableinventory]",
      item?.Inventory?.availableinventory || item?.availableinventory || 0
    );
    formData.append("data[Inventory][skunit_id]", item?.Skunit?.id || item?.id);
    formData.append("data[Inventory][batch_id]", "0");
    formData.append("data[Inventory][saleable]", "1");
    formData.append("data[inventory][reason_id]", "0");

    axios
      .post("/inventories/updateinv/1", formData)
      .then((data) => {
        if (data.data.Result === "true") {
          openSnackBar(QUANTITY_UPDATED_SUCCESSFULLY, "success");
        } else {
          openSnackBar(SOMETHING_WENT_WRONG, "error");
          setTempQuantity(
            item?.Inventory?.availableinventory || item?.availableinventory || 0
          );
        }
      })
      .catch((err) => {
        openSnackBar(SOMETHING_WENT_WRONG, "error");
        setTempQuantity(
          item?.Inventory?.availableinventory || item?.availableinventory || 0
        );
      });
  };

  const updatePrice = (id, price) => {
    let formData = new FormData();
    formData.append(
      "data[AllSave][warehouse]",
      item?.Inventory?.warehouse_id || warehouseId
    );
    formData.append("data[Save][0][skunit_id]", item?.Skunit?.id || item?.id);
    formData.append(
      "data[Save][0][availableinventory]",
      item?.Inventory?.availableinventory || item?.availableinventory || 0
    );
    formData.append("data[Save][0][value]", NaN);
    formData.append("data[Save][0][batch_id]", "0");
    formData.append("data[Save][0][saleable]", "1");
    formData.append("data[Save][0][reason_id]", "0");
    formData.append("data[WarehousePrice][0][updated_price]", price);

    const data = {
      AllSave: {
        warehouse: item?.Inventory?.warehouse_id || warehouseId,
      },
      Save: [
        {
          value: NaN,
          availableinventory:
            item?.Inventory?.availableinventory ||
            item?.availableinventory ||
            0,
          skunit_id: item?.Skunit?.id || item?.id,
          batch_id: 0,
          saleable: 1,
          reason_id: 0,
        },
      ],
      WarehousePrice: [
        {
          updated_price: price,
        },
      ],
    };

    axios
      .post(
        "inventories/updateinv/1?byPassRedirection=true&skipPayloadFormatting=true",
        data
      )
      .then((data) => {
        if (data.data.Result) {
          openSnackBar(PRICE_UPDATED_SUCCESSFULLY, "success");
        } else {
          openSnackBar(SOMETHING_WENT_WRONG, "error");
          setTempPrice(item?.Skunit?.price || item?.mrp);
        }
      })
      .catch((err) => {
        openSnackBar(SOMETHING_WENT_WRONG, "error");
        setTempPrice(item?.Skunit?.price || item?.mrp);
      });
  };

  const debouncedUpdateQuantity = useCallback(
    debounce(updateQuantity, 1000),
    []
  );
  const debouncedUpdatePrice = useCallback(debounce(updatePrice, 1000), []);

  if (item?.Skunit && !item.Skunit.active) {
    return;
  }

  return (
    <Grid item key={item.id} xs={12} md={6} lg={4} sx={{ zIndex: 1 }}>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: 2,
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
          marginX: 1,
          marginTop: 0.1,
          position: "relative",
        }}
      >
        {parseInt(tempQuantity, 10) === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "80px",
              left: "-20px",
              width: "150px",
              backgroundColor: "red",
              color: "white",
              fontWeight: "bold",
              zIndex: 10,
              textAlign: "center",
              transform: "rotate(-45deg)",
              transformOrigin: "top left",
              overflow: "visible",
            }}
          >
            Out of Stock
          </Box>
        )}
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            padding: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              flex: 1,
              alignSelf: "center",
            }}
            onClick={handleOpenDialog}
          >
            <img
              src={
                item?.Skunit?.pictureUrl ||
                item?.Skunit?.pictureurl ||
                item?.pictureurl
              }
              alt={"No Image"}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                width: "80px",
                height: "80px",
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 4,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 1,
                textAlign: "left",
                marginLeft: 2,
              }}
            >
              {item?.Skunit?.name || item?.name}
            </Typography>
            <Box
              sx={{
                marginTop: "5px",
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                marginLeft: 2,
              }}
            >
              <TextField
                type="number"
                id="outlined-required"
                label="Quantity"
                sx={{ m: 1, width: "12vh" }}
                size="small"
                // color='#00ddb1'
                value={tempQuantity}
                onChange={(e) =>
                  handleQuantityChange(
                    item?.Inventory?.id || item?.skunit_id,
                    parseInt(e.target.value, 10)
                  )
                }
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <TextField
                  id="outlined-required"
                  label="Selling Price"
                  type="number"
                  value={tempPrice}
                  sx={{ m: 1, width: "15vh" }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CurrencyRupeeIcon sx={{ fontSize: "small" }} />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) =>
                    handleMrpChange(
                      item?.Skunit?.id || item?.skunit_id,
                      parseFloat(e.target.value)
                    )
                  }
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    display: "-webkit-box",
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                    color: "grey",
                    textAlign: "center",
                  }}
                >
                  MRP: {NumberFormatUtils(item?.Skunit?.mrp || item?.mrp)}{" "}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginLeft: 2,
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#f0f4ff",
                  color: "#3f51b5",
                  padding: "3px 6px",
                  borderRadius: "4px",
                  marginLeft: 1,
                }}
              >
                {netQuantityValue}
              </Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                sx={{ marginLeft: "auto" }}
              >
                <Typography variant="body2" sx={{ marginRight: 1 }}>
                  In-stock
                </Typography>
                <AntSwitch
                  checked={tempQuantity > 0}
                  onChange={() =>
                    handleToggleChange(item?.Inventory?.id || item?.skunit_id)
                  }
                  name={`isEnabled-${item?.Inventory?.id || item?.skunit_id}`}
                  size="small"
                  color="primary"
                  sx={{ marginRight: 1 }}
                />
              </Stack>
            </Box>
          </Box>
        </CardContent>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg">
          <DialogTitle>
            {item?.Skunit?.name || item?.name}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: "absolute",
                right: 1,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <ImageSlider images={images} />
          <DialogContentText
            sx={{ margin: 2, maxHeight: "200px", overflow: "auto" }}
          >
            {showFullDescription ? (
              <Typography variant="body2">{description}</Typography>
            ) : (
              <Typography variant="body2">
                {description.length > MAX_DESCRIPTION_LENGTH
                  ? `${description.slice(0, MAX_DESCRIPTION_LENGTH)}...`
                  : description}
              </Typography>
            )}
            {description.length > MAX_DESCRIPTION_LENGTH && (
              <Button
                onClick={toggleDescription}
                size="small"
                sx={{ textTransform: "none" }}
              >
                {showFullDescription ? "See Less" : "See More"}
              </Button>
            )}
          </DialogContentText>
        </Dialog>
      </Card>
    </Grid>
  );
};

export default ProductCard;
